import React from "react";
import PropTypes from "prop-types";
import find from "lodash-es/find";

import Popover from "../popover/popover";
import Input from "../input/input";
import Icon from "../icon/icon";
import Tooltip from "../tooltip/tooltip";
import { ArrowRightWhite } from "../../lib/icons";
import {
  getValueInPx,
  isFunction,
  gatherClasses,
  debounce
} from "../../lib/utils";
import "./organizationSelect.scss";

const DROPDOWN_TRIGGER_WIDTH = 200;
const DROPDOWN_MENU_WIDTH = 162;
const DROPDOWN_MENU_MAX_HEIGHT = 200;
class OrganizationSelect extends React.Component {
  constructor(props, context) {
    super(props, context);

    const { value } = props;

    this.state = {
      isOpen: false,
      searchInputValue: "",
      selectedValue: value,
      filteredItems: props.items
    };

    this.triggerOrgIcon = React.createRef();
    this.trigglerLabel = React.createRef();
    this.listRef = React.createRef();
    this.debounced = debounce(this.saveScrollPosition, 300);
    this.scrollTop = 0;
  }

  static getDerivedStateFromProps(props, state) {
    return {
      ...state,
      selectedValue: props.value,
      filteredItems: OrganizationSelect.getFilteredItems(
        state.searchInputValue,
        props.items,
        props.filterOptions
      )
    };
  }

  saveScrollPosition = () => {
    if (this.listRef.current) {
      this.scrollTop = this.listRef.current.scrollTop;
    }
  };

  onItemClick = value => e => {
    const { onChange, footerItem } = this.props;
    const { filteredItems: items } = this.state;
    const allItems = [...items, footerItem].filter(Boolean);
    const selectedItem = find(allItems, { value });

    this.setState({ selectedValue: value });
    this.togglePopover(e);

    if (isFunction(onChange)) {
      onChange(selectedItem);
    }
  };

  getTriggerLabelStyle = () => {
    const { triggerWidth, isResponsive } = this.props;

    if (isResponsive) {
      return null;
    }

    return {
      maxWidth: getValueInPx(triggerWidth)
    };
  };

  getPopoverBodyStyle = () => {
    const { isResponsive, maxMenuWidth } = this.props;

    if (isResponsive) {
      return null;
    }

    const style = {
      minWidth: getValueInPx(DROPDOWN_MENU_WIDTH),
      maxWidth: getValueInPx(DROPDOWN_MENU_WIDTH)
    };

    if (maxMenuWidth) {
      style.maxWidth = getValueInPx(maxMenuWidth);
    }

    return style;
  };

  togglePopover = e => {
    const { target } = e || {};
    const { isOpen } = this.state;
    const { onTriggerLabelClick } = this.props;

    if (
      isFunction(onTriggerLabelClick) &&
      target instanceof Node &&
      (this.triggerOrgIcon.current.contains(target) ||
        this.trigglerLabel.current.contains(target))
    ) {
      onTriggerLabelClick();
      return;
    }

    // Dont close in case of clear icon click
    if (
      target.classList.contains("oui-search-input-clear") ||
      target.parentNode.classList.contains("oui-search-input-clear") ||
      target.parentNode.classList.contains("oui-clear-icon")
    ) {
      return;
    }

    this.setState({ isOpen: !isOpen });

    if (this.listRef.current) {
      this.listRef.current.scrollTop = this.scrollTop;
    }
  };

  getDropdownItem = item => {
    const { label, renderDropdownItem } = item;
    return isFunction(renderDropdownItem) ? renderDropdownItem(item) : label;
  };

  getTriggerLabel = item => {
    const { label, renderSelectedLabel } = item;
    return isFunction(renderSelectedLabel) ? renderSelectedLabel(item) : label;
  };

  getTriggerTooltip = item => {
    const { label, getSelectedLabelTooltipText } = item;
    return isFunction(getSelectedLabelTooltipText)
      ? getSelectedLabelTooltipText(item)
      : label;
  };

  static getFilteredItems = (value, items, filterOptions) => {
    return !value
      ? items
      : isFunction(filterOptions)
      ? filterOptions(value, items)
      : items.filter(
          item =>
            !item.label ||
            item.label.toLowerCase().includes(value.toLowerCase())
        );
  };

  onSearch = e => {
    const { items, filterOptions } = this.props;
    const { value } = e.target;
    this.setState({
      searchInputValue: value,
      filteredItems: OrganizationSelect.getFilteredItems(
        value,
        items,
        filterOptions
      )
    });
  };

  renderPopoverHeader = () => {
    const { header } = this.props;
    return header ? (
      <div className="oui-hosp-popover-header">{header}</div>
    ) : null;
  };

  renderSearchInput = () => {
    const { searchInputValue } = this.state;
    const { searchable, items, searchPlaceholder } = this.props;

    return searchable ? (
      <div className="oui-hosp-popover-search">
        <Input
          type="search"
          placeholder={searchPlaceholder}
          showClearIcon={searchInputValue.length > 0}
          value={searchInputValue}
          onClear={e => {
            e.stopPropagation();
            this.setState({ filteredItems: items, searchInputValue: "" });
          }}
          onChange={this.onSearch}
        />
      </div>
    ) : null;
  };

  renderPopoverBody = () => {
    const { maxMenuHeight, retainScroll } = this.props;
    const { filteredItems: items } = this.state;
    const { selectedValue } = this.state;
    const bodyStyle = {};

    if (maxMenuHeight) {
      bodyStyle.maxHeight = getValueInPx(maxMenuHeight);
    }

    return items && items.length ? (
      <ul
        className="oui-hosp-popover-body"
        style={bodyStyle}
        ref={this.listRef}
        onScroll={retainScroll ? this.saveScrollPosition : null}
      >
        {items.map((item, index) => {
          return (
            <li
              tabIndex="-1"
              className={gatherClasses(
                null,
                "oui-hosp-popover-item",
                selectedValue === item.value && "oui-active",
                item.disabled && "oui-disabled"
              )}
              key={`hosp-item-${index}`}
              onClick={!item.disabled ? this.onItemClick(item.value) : null}
              aria-hidden="true"
            >
              {this.getDropdownItem(item)}
            </li>
          );
        })}
      </ul>
    ) : null;
  };

  renderPopoverFooter = () => {
    const { footerItem } = this.props;
    return footerItem ? (
      <div
        tabIndex="-1"
        role="button"
        className="oui-hosp-popover-footer"
        onClick={this.onItemClick(footerItem.value)}
        aria-hidden="true"
      >
        {isFunction(footerItem.renderDropdownItem)
          ? footerItem.renderDropdownItem(footerItem)
          : footerItem.label}
      </div>
    ) : null;
  };

  renderTrigger = () => {
    const { footerItem, disabled, items } = this.props;
    const { selectedValue, isOpen } = this.state;
    const allItems = [...items, footerItem].filter(Boolean);
    const selectedItem = find(allItems, { value: selectedValue });
    const triggerLabelStyle = this.getTriggerLabelStyle();
    let triggerIcon = null;
    let triggerLabel = null;
    let triggerTooltip = null;

    if (selectedItem) {
      triggerIcon = selectedItem.icon;
      triggerLabel = this.getTriggerLabel(selectedItem);
      triggerTooltip = this.getTriggerTooltip(selectedItem);
    }

    return (
      <div
        className={gatherClasses(
          null,
          "oui-hos-trigger",
          disabled && "oui-disabled"
        )}
      >
        {triggerIcon ? (
          <div className="oui-hos-trigger-icon" ref={this.triggerOrgIcon}>
            {React.isValidElement(triggerIcon) ? (
              React.cloneElement(triggerIcon, {
                muted: false,
                className: gatherClasses(
                  null,
                  triggerIcon.props.className,
                  "oui-hos-toggler-icon"
                )
              })
            ) : (
              <Icon {...triggerIcon} size="sm" muted={false} />
            )}
          </div>
        ) : null}
        <Tooltip
          text={triggerTooltip}
          overflowOnly
          key={`hosp-tooltip-${selectedValue}`}
        >
          <div
            style={triggerLabelStyle}
            className="oui-hos-trigger-label oui-text-truncate"
            ref={this.trigglerLabel}
          >
            {triggerLabel}
          </div>
        </Tooltip>
        <div className="oui-hos-trigger-toggler">
          <Icon
            src={ArrowRightWhite}
            className={`oui-${
              isOpen ? "opened" : "closed"
            } oui-hos-toggler-icon`}
            size="xs"
            muted={false}
          />
        </div>
      </div>
    );
  };

  render() {
    const { isResponsive, disabled } = this.props;
    const { isOpen } = this.state;
    const bodyContentStyle = this.getPopoverBodyStyle();

    return (
      <Popover
        disabled={disabled}
        isOpen={isOpen}
        toggle={this.togglePopover}
        hideOnOutsideClick
        placement="bottom-end"
        className={gatherClasses(
          null,
          "oui-headerbar-org-select-popover",
          isResponsive && "oui-m-0",
          isResponsive && "oui-is-responsive"
        )}
        bodyClassName="oui-hosp-popover-content"
        body={() => (
          <div className="oui-hosp-content" style={bodyContentStyle}>
            {this.renderPopoverHeader()}
            {this.renderSearchInput()}
            {this.renderPopoverBody()}
            {this.renderPopoverFooter()}
          </div>
        )}
        hideArrow
      >
        {this.renderTrigger()}
      </Popover>
    );
  }
}

const DropdownItemType = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  icon: PropTypes.shape({
    src: PropTypes.string,
    svg: PropTypes.string,
    classes: PropTypes.string
  }),
  renderDropdownItem: PropTypes.func,
  renderSelectedLabel: PropTypes.func,
  getSelectedLabelTooltipText: PropTypes.func,
  disabled: PropTypes.bool
});

OrganizationSelect.propTypes = {
  isResponsive: PropTypes.bool,
  triggerWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxMenuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxMenuHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  header: PropTypes.string,
  items: PropTypes.arrayOf(DropdownItemType),
  footerItem: DropdownItemType,
  disabled: PropTypes.bool,
  onTriggerLabelClick: PropTypes.func,
  searchable: PropTypes.bool,
  filterOptions: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  retainScroll: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool
  ])
};

OrganizationSelect.defaultProps = {
  triggerWidth: DROPDOWN_TRIGGER_WIDTH,
  maxMenuWidth: DROPDOWN_MENU_WIDTH,
  maxMenuHeight: DROPDOWN_MENU_MAX_HEIGHT,
  isResponsive: false,
  searchable: false,
  retainScroll: false
};

OrganizationSelect.displayName = "OrionHeaderbarOrganizationSelect";

export default OrganizationSelect;
